import {
  AlertTooltip,
  Button,
  IconLoadingSpinner,
  Select,
  TextField,
  Typography,
  helperTextTypography,
  mediumBodyLargeTypography,
  pxToRem,
  spacing2,
  spacing4,
  spacing5,
  spacing6,
} from '@leaftrade/leaftrade-component-library';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useStorefrontState } from '../../../hooks/useReduxState';
import {
  addTimedBanner,
  clearCartAfterCheckout,
  getShopperLocations,
  postCheckoutData,
  setLocationFromCart,
} from '../../../store/actions/storefrontActions';
import {
  CheckoutActionsLeafPayWrapper,
  CheckoutActionsMessageWrapper,
  CheckoutActionsRequestWrapper,
  CheckoutActionsRequestTooltipWrapper,
} from './CheckoutActions.styles';
import CheckoutMessageTabs, { MESSAGE_TAB } from './CheckoutMessageTabs';

const CHECKOUT_MESSAGE_MAX_LENGTH = 1000;

const CheckoutActions = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cart, checkout, shopper, vendor } = useStorefrontState();
  const { multiCartSummary } = cart;
  const { canPlaceOrder, isAdmin, isPostingCheckoutData, leafPayData } =
    checkout;
  const { currentLocation } = shopper;
  const { slug } = vendor;

  const defaultAccount =
    leafPayData?.bankAccounts.find((account) => account.default)?.id ||
    leafPayData?.bankAccounts[0]?.id ||
    null;

  const [orderNotes, setOrderNotes] = useState('');
  const [vendorMessage, setVendorMessage] = useState('');
  const [activeMessageTab, setActiveMessageTab] = useState(MESSAGE_TAB.TEAM);
  const [selectedAccount, setSelectedAccount] = useState(defaultAccount);
  const [requestCartTooltipOpen, setRequestCartTooltipOpen] = useState(false);

  const ableToOrder = !canPlaceOrder
    ? false
    : canPlaceOrder && leafPayData.isEnabled
      ? leafPayData.bankAccounts?.length > 0
      : true;

  const showMessage = ({ messages, preventAutoClose, severity }) => {
    messages.forEach((message) => {
      dispatch(
        addTimedBanner({
          message,
          preventAutoClose,
          severity,
        })
      );
    });
  };

  const handlePostCheckout = (isRequest = false) => {
    const data = {
      action: isRequest ? 'request' : 'order',
      bank_account: leafPayData.isEnabled ? Number(selectedAccount) : null,
      'note-message': orderNotes,
      ...(isRequest ? {} : { 'message-to-vendor': vendorMessage }),
    };

    dispatch(
      postCheckoutData({
        data,
        onSuccess: async ({
          errors,
          success_messages: successMessages,
          warning_messages: warningMessages,
        }) => {
          if (errors?.length > 0) {
            showMessage({
              messages: errors,
              preventAutoClose: true,
              severity: 'error',
            });
          }
          if (warningMessages?.length > 0) {
            showMessage({
              messages: warningMessages,
              timeout: 7000,
              severity: 'warning',
            });
          }
          if (successMessages?.length > 0) {
            await dispatch(clearCartAfterCheckout());
            showMessage({ messages: successMessages, severity: 'success' });
            const newCart = multiCartSummary?.carts?.find(
              (cartSummary) =>
                Number(cartSummary.locationId) !== Number(currentLocation.id)
            );

            if (newCart) {
              await dispatch(getShopperLocations({ slug }));
              await dispatch(
                setLocationFromCart({
                  locationId: newCart?.locationId,
                  dispensaryId: shopper.dispensary.id,
                  vendorSlug: slug,
                })
              );
            }

            const targetPath = newCart
              ? `/storefront/sellers/${slug}/cart/`
              : `/storefront/sellers/${slug}/`;
            await navigate(targetPath);
          }
        },
        slug,
      })
    );
  };

  const isVendorMessageTab = activeMessageTab === MESSAGE_TAB.VENDOR;
  const hasVendorMessage = vendorMessage.trim().length > 0;

  return (
    <div data-testid='checkout-actions--component--div'>
      <Typography
        bodyStyle={helperTextTypography}
        italic
        sx={{ padding: `${spacing4} 0` }}
      >
        Submitted order quantities are subject to the seller&apos;s packaged
        inventory availability and are not always guaranteed. You will be
        notified if the vendor makes any changes to your order.
      </Typography>
      {isPostingCheckoutData ? (
        <Typography
          align='center'
          bodyStyle={mediumBodyLargeTypography}
          sx={{
            padding: `${spacing2} 0`,
            display: 'flex',
            alignItems: 'center',
            width: 'fit-content',
            margin: `${spacing5} auto`,
            '& > div': {
              marginLeft: spacing5,
            },
          }}
        >
          Processing order...
          <IconLoadingSpinner />
        </Typography>
      ) : (
        <>
          {leafPayData.isEnabled && (
            <CheckoutActionsLeafPayWrapper>
              <Typography bodyStyle={helperTextTypography} italic>
                This vendor requires you to pay for your order via the Leaf
                Trade platform. Please select your account below. By placing
                this order you agree to the{' '}
                <a
                  target='_blank'
                  href='https://www.leaflink.com/legal/leaflink-terms-of-service/'
                  rel='noreferrer'
                >
                  terms of service
                </a>
              </Typography>
              {leafPayData.bankAccounts.length > 0 && (
                <Select
                  label='Select Account'
                  options={leafPayData.bankAccounts.map((account) => ({
                    label: account.displayName,
                    selected: account.id === selectedAccount,
                    value: account.id,
                  }))}
                  onSelect={({ value }) => {
                    setSelectedAccount(value);
                  }}
                  sx={{
                    maxWidth: pxToRem(650),
                    margin: '0 auto',
                    width: '100%',
                  }}
                />
              )}
              {!isAdmin && (
                <Typography bodyStyle={helperTextTypography} italic>
                  Please contact your account administrator to add or activate a
                  bank account.
                </Typography>
              )}
            </CheckoutActionsLeafPayWrapper>
          )}
          {ableToOrder && (
            <Button
              onClick={() => handlePostCheckout(false)}
              sx={{
                margin: `${spacing5} auto ${spacing6}`,
                width: pxToRem(200),
              }}
            >
              Order
            </Button>
          )}
          <CheckoutActionsMessageWrapper>
            {ableToOrder && (
              <CheckoutMessageTabs
                activeTab={activeMessageTab}
                onTabChange={setActiveMessageTab}
              />
            )}
            <TextField
              label={
                ableToOrder && isVendorMessageTab
                  ? 'Message to vendor'
                  : 'Message to your team'
              }
              maxLength={CHECKOUT_MESSAGE_MAX_LENGTH}
              message={
                ableToOrder && isVendorMessageTab
                  ? 'Include a message for the seller with this order'
                  : 'Add a note for your team members'
              }
              multiline
              onChange={({ target }) => {
                const nextValue = target.value.slice(
                  0,
                  CHECKOUT_MESSAGE_MAX_LENGTH
                );

                if (ableToOrder && isVendorMessageTab) {
                  setVendorMessage(nextValue);
                  return;
                }

                setOrderNotes(nextValue);
              }}
              value={
                ableToOrder && isVendorMessageTab ? vendorMessage : orderNotes
              }
            />
          </CheckoutActionsMessageWrapper>
          <CheckoutActionsRequestWrapper>
            <Button
              disabled={hasVendorMessage}
              onClick={() => handlePostCheckout(true)}
              sx={{ width: pxToRem(200) }}
              variant={canPlaceOrder ? 'secondary' : 'primary'}
            >
              {canPlaceOrder ? 'Request Cart' : 'Submit For Approval'}
            </Button>
            {hasVendorMessage ? (
              <CheckoutActionsRequestTooltipWrapper>
                <AlertTooltip
                  alertType='info'
                  clickable
                  id='checkout-request-cart-disabled-tooltip'
                  isOpen={requestCartTooltipOpen}
                  setIsOpen={setRequestCartTooltipOpen}
                  tooltipPlacement='right'
                >
                  Request Cart is unavailable while a vendor message is entered.
                  Vendor messages are only sent when placing an order.
                </AlertTooltip>
              </CheckoutActionsRequestTooltipWrapper>
            ) : null}
          </CheckoutActionsRequestWrapper>
        </>
      )}
      {leafPayData.isEnabled && (
        <Typography
          bodyStyle={helperTextTypography}
          italic
          sx={{ padding: `${spacing4} 0` }}
        >
          Direct Pay is 100% free for buyers on Leaf Trade. No hidden fees.
        </Typography>
      )}
    </div>
  );
};

export default CheckoutActions;
